/**
 * DV-002: Logs Panel Component
 * Virtualized logs display with filtering and controls
 */

import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Filter, Copy, Trash2, Search, Download, BarChart3, Shield, Eye, AlertTriangle, FileText } from 'lucide-react';
import { cn } from '@/utils/cn';
import { centralLogger } from '@/lib/centralized-logger';
import { redactLogEntry } from '@/lib/redaction';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  id?: string;
  ts: string;
  level: LogLevel;
  text: string;
  meta?: Record<string, any>;
}

export interface LogsPanelProps {
  entries: LogEntry[];
  onClear?: () => void;
  onCopy?: (logs: LogEntry[]) => void;
  onDownload?: (format: 'json' | 'csv' | 'txt') => void;
  showStats?: boolean;
  enableDownload?: boolean;
  enableRedaction?: boolean; // Enable redaction controls
  defaultRedacted?: boolean; // Default to redacted view
  adminRawAccess?: boolean; // Allow admin to see raw data
  className?: string;
  minHeight?: number;
  maxHeight?: number;
  'data-testid'?: string;
}

const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  debug: 'var(--fg-muted)',
  info: 'var(--accent)',
  warn: 'var(--warning)',
  error: 'var(--danger)',
};

const ITEM_HEIGHT = 24; // Height of each log entry in pixels
const BUFFER_SIZE = 5; // Extra items to render outside visible area

export function LogsPanel({
  entries,
  onClear,
  onCopy,
  onDownload,
  showStats = false,
  enableDownload = true,
  enableRedaction = true,
  defaultRedacted = true,
  adminRawAccess = false,
  className,
  minHeight = 200,
  maxHeight = 400,
  'data-testid': testId = 'logs-panel'
}: LogsPanelProps) {
  const [errorOnlyFilter, setErrorOnlyFilter] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [correlationFilter, setCorrelationFilter] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showStatsPanel, setShowStatsPanel] = useState(false);

  // Redaction state
  const [isRedacted, setIsRedacted] = useState(defaultRedacted);
  const [showRawConfirmation, setShowRawConfirmation] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  // Filter and redact logs based on level, search, and redaction settings
  const filteredEntries = useMemo(() => {
    let filtered = entries;

    if (errorOnlyFilter) {
      filtered = filtered.filter(entry => entry.level === 'error');
    }

    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.text.toLowerCase().includes(searchLower) ||
        entry.level.toLowerCase().includes(searchLower) ||
        (entry.meta && JSON.stringify(entry.meta).toLowerCase().includes(searchLower))
      );
    }

    if (correlationFilter.trim()) {
      const correlationLower = correlationFilter.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.meta?.correlationId?.toLowerCase().includes(correlationLower) ||
        entry.meta?.requestId?.toLowerCase().includes(correlationLower)
      );
    }

    // Apply redaction if enabled
    if (enableRedaction && isRedacted) {
      filtered = filtered.map(entry => redactLogEntry(entry, true));
    }

    return filtered;
  }, [entries, errorOnlyFilter, searchFilter, correlationFilter, enableRedaction, isRedacted]);

  // Fixed height to prevent reflow - always use maxHeight for consistency
  const containerHeight = maxHeight;
  const visibleCount = Math.ceil(containerHeight / ITEM_HEIGHT);
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
  const endIndex = Math.min(filteredEntries.length, startIndex + visibleCount + BUFFER_SIZE * 2);
  const visibleEntries = filteredEntries.slice(startIndex, endIndex);

  // Reset scroll when filters change to avoid empty viewport
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
      setScrollTop(0);
    }
  }, [errorOnlyFilter, searchFilter, correlationFilter]);

  // Handle redaction toggle
  const handleRedactionToggle = useCallback(() => {
    if (!enableRedaction) return;

    if (isRedacted) {
      // Switching to raw view - show confirmation for admin users
      if (adminRawAccess) {
        setShowRawConfirmation(true);
      }
    } else {
      // Switching to redacted view - always allowed
      setIsRedacted(true);
    }
  }, [enableRedaction, isRedacted, adminRawAccess]);

  const confirmShowRaw = useCallback(() => {
    setIsRedacted(false);
    setShowRawConfirmation(false);
  }, []);

  const cancelShowRaw = useCallback(() => {
    setShowRawConfirmation(false);
  }, []);

  // Handle scroll for virtualization
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  // Copy all visible logs to clipboard
  const copyLogs = useCallback(async () => {
    if (onCopy) {
      onCopy(filteredEntries);
      return;
    }

    // Default copy behavior
    const logText = filteredEntries
      .map(entry => `${entry.ts} [${entry.level.toUpperCase()}] ${entry.text}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(logText);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = logText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, [filteredEntries, onCopy]);

  // Clear all logs (callback to parent)
  const clearLogs = useCallback(() => {
    if (onClear) {
      onClear();
    }
  }, [onClear]);


  // Copy correlation blocks as JSON
  const copyCorrelationBlocks = useCallback(async () => {
    const correlationBlocks: Record<string, LogEntry[]> = {};

    filteredEntries.forEach(entry => {
      const correlationId = entry.meta?.correlationId || entry.meta?.requestId;
      if (correlationId) {
        if (!correlationBlocks[correlationId]) {
          correlationBlocks[correlationId] = [];
        }
        correlationBlocks[correlationId].push(entry);
      }
    });

    const jsonOutput = {
      timestamp: new Date().toISOString(),
      total_correlations: Object.keys(correlationBlocks).length,
      total_entries: filteredEntries.length,
      correlations: correlationBlocks
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(jsonOutput, null, 2));
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = JSON.stringify(jsonOutput, null, 2);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, [filteredEntries]);

  // Quick filter by specific correlation ID
  const quickFilterByCorrelation = useCallback((correlationId: string) => {
    setCorrelationFilter(correlationId);
  }, []);

  // Download logs in specified format
  const downloadLogs = useCallback((format: 'json' | 'csv' | 'txt') => {
    if (onDownload) {
      onDownload(format);
    } else {
      // Use central logger's download functionality
      centralLogger.downloadLogs(format);
    }
    setShowDownloadMenu(false);
  }, [onDownload]);

  // Get log statistics
  const logStats = useMemo(() => {
    const levelCounts = entries.reduce((acc, entry) => {
      acc[entry.level] = (acc[entry.level] || 0) + 1;
      return acc;
    }, {} as Record<LogLevel, number>);

    const totalSize = entries.reduce((size, entry) => {
      return size + JSON.stringify(entry).length;
    }, 0);

    return {
      total: entries.length,
      filtered: filteredEntries.length,
      levels: levelCounts,
      sizeMB: Math.round(totalSize / 1024 / 1024 * 100) / 100,
      oldestEntry: entries[0]?.ts,
      newestEntry: entries[entries.length - 1]?.ts
    };
  }, [entries, filteredEntries.length]);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDownloadMenu]);

  // Format timestamp for display
  const formatTimestamp = (ts: string): string => {
    try {
      const date = new Date(ts);
      return date.toLocaleTimeString();
    } catch {
      return ts;
    }
  };

  // Check if entry has API error metadata
  const isApiError = (entry: LogEntry): boolean => {
    return entry.level === 'error' &&
           !!entry.meta &&
           ('endpoint' in entry.meta || 'status' in entry.meta || 'correlationId' in entry.meta);
  };

  // Format structured error metadata for display
  const formatErrorMeta = (meta: Record<string, any>): string => {
    const parts: string[] = [];

    if (meta.endpoint) parts.push(`${meta.endpoint}`);
    if (meta.status) parts.push(`HTTP ${meta.status}`);
    if (meta.type) parts.push(`(${meta.type})`);
    if (meta.correlationId) parts.push(`[${meta.correlationId.slice(-8)}]`);

    return parts.join(' ');
  };

  return (
    <div
      className={cn("border rounded-lg overflow-hidden", className)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-default)',
        minHeight: `${minHeight}px`,
      }}
      data-testid={testId}
    >
      {/* Controls Header */}
      <div
        className="px-4 py-3 border-b"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="flex items-center justify-between gap-4">
          <h3
            className="text-sm font-mono uppercase tracking-wider"
            style={{
              fontFamily: 'var(--font-family-mono)',
              color: 'var(--accent)',
            }}
          >
            System Logs ({filteredEntries.length})
          </h3>

          <div className="flex items-center gap-3">
            {/* Search Filter */}
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3"
                style={{ color: 'var(--fg-muted)' }}
              />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-7 pr-2 py-1 text-xs rounded border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`${testId}-search`}
              />
            </div>

            {/* Correlation Filter */}
            <div className="relative">
              <input
                type="text"
                placeholder="Filter by correlation ID..."
                value={correlationFilter}
                onChange={(e) => setCorrelationFilter(e.target.value)}
                className="pl-2 pr-2 py-1 text-xs rounded border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  borderColor: correlationFilter ? 'var(--accent)' : 'var(--border-default)',
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                  minWidth: '140px'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`${testId}-correlation-filter`}
                title="Filter logs by correlation ID or request ID"
              />
              {correlationFilter && (
                <button
                  onClick={() => setCorrelationFilter('')}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full hover:bg-opacity-20"
                  style={{
                    backgroundColor: 'var(--fg-muted)',
                    color: 'var(--bg-surface)'
                  }}
                  title="Clear correlation filter"
                >
                  ×
                </button>
              )}
            </div>

            {/* Error Filter Toggle */}
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={errorOnlyFilter}
                onChange={(e) => setErrorOnlyFilter(e.target.checked)}
                className="w-3 h-3 rounded focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--accent)',
                }}
                data-testid={`${testId}-errors-only`}
              />
              <Filter className="w-3 h-3" style={{ color: 'var(--fg-muted)' }} />
              <span
                className="font-mono"
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  color: 'var(--fg-muted)',
                }}
              >
                Errors Only
              </span>
            </label>

            {/* Redaction Toggle */}
            {enableRedaction && (
              <button
                onClick={handleRedactionToggle}
                className="flex items-center gap-2 px-2 py-1 text-xs rounded border transition-colors focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: isRedacted ? 'var(--bg-surface)' : 'var(--accent-subtle)',
                  borderColor: isRedacted ? 'var(--border-default)' : 'var(--accent)',
                  color: isRedacted ? 'var(--fg-muted)' : 'var(--accent)',
                  fontFamily: 'var(--font-family-mono)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                title={isRedacted ? 'Show raw log data (admin only)' : 'Switch to redacted view'}
                data-testid={`${testId}-redaction-toggle`}
              >
                {isRedacted ? (
                  <>
                    <Shield className="w-3 h-3" />
                    <span>Redacted</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-3 h-3" />
                    <span>Raw</span>
                  </>
                )}
              </button>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={copyLogs}
                className="p-1 rounded hover:bg-opacity-10 transition-colors focus:outline-none focus:ring-2"
                style={{
                  color: 'var(--fg-muted)',
                  ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                title="Copy all logs"
                data-testid={`${testId}-copy`}
              >
                <Copy className="w-3 h-3" />
              </button>

              <button
                onClick={copyCorrelationBlocks}
                className="p-1 rounded hover:bg-opacity-10 transition-colors focus:outline-none focus:ring-2"
                style={{
                  color: 'var(--fg-muted)',
                  ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                title="Copy correlation blocks as JSON"
                data-testid={`${testId}-copy-json`}
              >
                <FileText className="w-3 h-3" />
              </button>

              <button
                onClick={clearLogs}
                className="p-1 rounded hover:bg-opacity-10 transition-colors focus:outline-none focus:ring-2"
                style={{
                  color: 'var(--fg-muted)',
                  ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                title="Clear logs"
                data-testid={`${testId}-clear`}
              >
                <Trash2 className="w-3 h-3" />
              </button>

              {/* Download Button */}
              {enableDownload && (
                <div className="relative" ref={downloadMenuRef}>
                  <button
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                    className="p-1 rounded hover:bg-opacity-10 transition-colors focus:outline-none focus:ring-2"
                    style={{
                      color: 'var(--fg-muted)',
                      ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                      ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    title="Download logs"
                    data-testid={`${testId}-download`}
                  >
                    <Download className="w-3 h-3" />
                  </button>

                  {/* Download Menu */}
                  {showDownloadMenu && (
                    <div
                      className="absolute right-0 top-full mt-1 bg-white border rounded shadow-lg z-50 min-w-24"
                      style={{
                        backgroundColor: 'var(--bg-surface)',
                        borderColor: 'var(--border-default)',
                      }}
                    >
                      <button
                        onClick={() => downloadLogs('json')}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 first:rounded-t last:rounded-b"
                        style={{ color: 'var(--fg-primary)' }}
                      >
                        JSON
                      </button>
                      <button
                        onClick={() => downloadLogs('csv')}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 border-t first:rounded-t last:rounded-b"
                        style={{
                          color: 'var(--fg-primary)',
                          borderColor: 'var(--border-subtle)'
                        }}
                      >
                        CSV
                      </button>
                      <button
                        onClick={() => downloadLogs('txt')}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 border-t first:rounded-t last:rounded-b"
                        style={{
                          color: 'var(--fg-primary)',
                          borderColor: 'var(--border-subtle)'
                        }}
                      >
                        TXT
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Stats Button */}
              {showStats && (
                <button
                  onClick={() => setShowStatsPanel(!showStatsPanel)}
                  className="p-1 rounded hover:bg-opacity-10 transition-colors focus:outline-none focus:ring-2"
                  style={{
                    color: showStatsPanel ? 'var(--accent)' : 'var(--fg-muted)',
                    ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                    ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  title="Show log statistics"
                  data-testid={`${testId}-stats`}
                >
                  <BarChart3 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && showStatsPanel && (
        <div
          className="px-4 py-3 border-b"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
          }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <div
                className="font-mono uppercase tracking-wider text-xs mb-1"
                style={{ color: 'var(--fg-muted)' }}
              >
                Logs
              </div>
              <div style={{ color: 'var(--fg-primary)' }}>
                {logStats.filtered} / {logStats.total}
              </div>
            </div>

            <div>
              <div
                className="font-mono uppercase tracking-wider text-xs mb-1"
                style={{ color: 'var(--fg-muted)' }}
              >
                Memory
              </div>
              <div style={{ color: 'var(--fg-primary)' }}>
                {logStats.sizeMB} MB
              </div>
            </div>

            <div>
              <div
                className="font-mono uppercase tracking-wider text-xs mb-1"
                style={{ color: 'var(--fg-muted)' }}
              >
                Levels
              </div>
              <div style={{ color: 'var(--fg-primary)' }}>
                {Object.entries(logStats.levels).map(([level, count]) => (
                  <span key={level} className="mr-2">
                    <span style={{ color: LOG_LEVEL_COLORS[level as LogLevel] }}>
                      {level}
                    </span>: {count}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div
                className="font-mono uppercase tracking-wider text-xs mb-1"
                style={{ color: 'var(--fg-muted)' }}
              >
                Time Range
              </div>
              <div style={{ color: 'var(--fg-primary)' }}>
                {logStats.oldestEntry && logStats.newestEntry ? (
                  <>
                    {formatTimestamp(logStats.oldestEntry)} -{' '}
                    {formatTimestamp(logStats.newestEntry)}
                  </>
                ) : (
                  'No logs'
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Virtualized Log Container */}
      <div
        ref={scrollContainerRef}
        className="overflow-auto"
        style={{
          height: `${containerHeight}px`,
          backgroundColor: 'var(--bg-app)',
        }}
        onScroll={handleScroll}
        data-testid={`${testId}-container`}
      >
        {filteredEntries.length === 0 ? (
          <div
            className="flex items-center justify-center h-full text-sm italic"
            style={{ color: 'var(--fg-muted)' }}
          >
            No logs to display
          </div>
        ) : (
          <div
            style={{
              height: `${filteredEntries.length * ITEM_HEIGHT}px`,
              position: 'relative',
            }}
          >
            {visibleEntries.map((entry, index) => {
              const actualIndex = startIndex + index;
              const entryId = entry.id || `${entry.ts}-${actualIndex}`;

              return (
                <div
                  key={entryId}
                  className="absolute left-0 right-0 px-3 py-1 flex items-start gap-3 text-xs font-mono border-b last:border-b-0"
                  style={{
                    top: `${actualIndex * ITEM_HEIGHT}px`,
                    height: `${ITEM_HEIGHT}px`,
                    fontFamily: 'var(--font-family-mono)',
                    borderColor: 'var(--border-subtle)',
                  }}
                >
                  <span
                    className="w-16 flex-shrink-0 text-xs"
                    style={{ color: 'var(--fg-muted)' }}
                  >
                    {formatTimestamp(entry.ts)}
                  </span>
                  <span
                    className="w-12 flex-shrink-0 uppercase font-medium"
                    style={{ color: LOG_LEVEL_COLORS[entry.level] }}
                  >
                    {entry.level}
                  </span>
                  <div className="flex-1 break-words">
                    <span style={{ color: 'var(--fg-primary)' }}>
                      {entry.text}
                    </span>
                    {/* Enhanced display for API errors */}
                    {isApiError(entry) && entry.meta && (
                      <div
                        className="text-xs mt-1 opacity-80"
                        style={{ color: 'var(--fg-muted)' }}
                      >
                        {formatErrorMeta(entry.meta)}
                        {entry.meta.retryable && (
                          <span className="ml-2 px-1 rounded" style={{
                            backgroundColor: 'var(--warning)',
                            color: 'var(--bg-app)',
                            fontSize: '10px'
                          }}>
                            RETRYABLE
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Clickable Correlation ID Badge */}
                  {(entry.meta?.correlationId || entry.meta?.requestId) && (
                    <button
                      onClick={() => quickFilterByCorrelation(entry.meta?.correlationId || entry.meta?.requestId)}
                      className="text-xs px-1 py-0.5 rounded border transition-colors hover:bg-opacity-20"
                      style={{
                        backgroundColor: correlationFilter === (entry.meta?.correlationId || entry.meta?.requestId)
                          ? 'var(--accent-subtle)'
                          : 'var(--bg-surface)',
                        borderColor: 'var(--accent)',
                        color: 'var(--accent)',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: '10px'
                      }}
                      title={`Filter by ${entry.meta?.correlationId ? 'correlation' : 'request'} ID: ${entry.meta?.correlationId || entry.meta?.requestId}`}
                    >
                      {entry.meta?.correlationId ? 'C' : 'R'}:{(entry.meta?.correlationId || entry.meta?.requestId).slice(-6)}
                    </button>
                  )}

                  {entry.meta && Object.keys(entry.meta).length > 0 && !isApiError(entry) && (
                    <span
                      className="text-xs opacity-60"
                      style={{ color: 'var(--fg-muted)' }}
                      title={JSON.stringify(entry.meta, null, 2)}
                    >
                      +{Object.keys(entry.meta).length}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Raw View Confirmation Dialog */}
      {showRawConfirmation && enableRedaction && adminRawAccess && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelShowRaw}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border shadow-lg"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-default)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle
                className="w-5 h-5 flex-shrink-0"
                style={{ color: 'var(--warning)' }}
              />
              <h3
                className="text-lg font-semibold"
                style={{
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Show Raw Log Data?
              </h3>
            </div>

            <p
              className="text-sm mb-6 leading-relaxed"
              style={{ color: 'var(--fg-muted)' }}
            >
              You are about to view unredacted log data that may contain sensitive information
              such as API keys, user data, or system internals. This should only be used for
              debugging purposes by authorized administrators.
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={cancelShowRaw}
                className="px-4 py-2 text-sm rounded border transition-colors focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-muted)',
                  fontFamily: 'var(--font-family-mono)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Cancel
              </button>

              <button
                onClick={confirmShowRaw}
                className="px-4 py-2 text-sm rounded border transition-colors focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--warning)',
                  borderColor: 'var(--warning)',
                  color: 'var(--bg-app)',
                  fontFamily: 'var(--font-family-mono)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Show Raw Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}